/**
 * NotificationSettingsScreen.jsx
 * Benachrichtigungs-Einstellungen für Mobile App
 * Kunden können E-Mail/WhatsApp/Push-Erinnerungen selbst verwalten
 */
import React, { useState, useEffect } from 'react';
import { 
  Bell, Mail, MessageCircle, Smartphone, Calendar, Megaphone,
  RefreshCw, Save, Check, ChevronRight, Info
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const NotificationSettingsScreen = ({ portalToken, customerId, onNavigate }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [prefs, setPrefs] = useState({
    // Termin-Bestätigungen
    email: true,
    whatsapp: true,
    // Termin-Erinnerungen (vor dem Termin)
    email_reminders: true,
    whatsapp_reminders: true,
    // Newsletter
    newsletter_email: true,
    newsletter_whatsapp: true,
    // Push (für zukünftige App)
    push: false
  });

  useEffect(() => {
    if (portalToken) loadPrefs();
  }, [portalToken]);

  const loadPrefs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/portal/auth/profile`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      
      const commPrefs = response.data?.communication_preferences || {};
      setPrefs({
        email: commPrefs.email !== false,
        whatsapp: commPrefs.whatsapp !== false,
        email_reminders: commPrefs.email_reminders !== false,
        whatsapp_reminders: commPrefs.whatsapp_reminders !== false,
        newsletter_email: commPrefs.newsletter_email !== false,
        newsletter_whatsapp: commPrefs.newsletter_whatsapp !== false,
        push: commPrefs.push === true
      });
    } catch (error) {
      console.error('Error loading preferences:', error);
      toast.error(t('notifications_screen.load_error'));
    } finally {
      setLoading(false);
    }
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/portal/auth/profile`, {
        communication_preferences: prefs
      }, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      
      toast.success(t('notifications_screen.saved'));
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error(t('notifications_screen.save_error'));
    } finally {
      setSaving(false);
    }
  };

  const togglePref = (key) => {
    setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const ToggleSwitch = ({ enabled, onToggle, testId }) => (
    <button
      onClick={onToggle}
      className={`w-12 h-7 rounded-full transition-all relative ${
        enabled ? 'bg-yellow-500' : 'bg-zinc-600'
      }`}
      data-testid={testId}
    >
      <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
        enabled ? 'left-6' : 'left-1'
      }`} />
    </button>
  );

  const SettingRow = ({ icon: Icon, iconColor, title, description, prefKey }) => (
    <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-xl">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconColor}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-white font-medium text-sm">{title}</p>
          <p className="text-zinc-500 text-xs">{description}</p>
        </div>
      </div>
      <ToggleSwitch 
        enabled={prefs[prefKey]} 
        onToggle={() => togglePref(prefKey)}
        testId={`toggle-${prefKey}`}
      />
    </div>
  );

  return (
    <div className="p-4 space-y-4 pb-24" data-testid="notification-settings-screen">
      {/* Header */}
      <div className="flex justify-between items-center pt-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500/30 to-orange-500/20 flex items-center justify-center">
            <Bell className="w-5 h-5 text-yellow-400" />
          </div>
          <div>
            <h1 className="text-white text-base font-bold">{t('notifications_screen.title')}</h1>
            <p className="text-zinc-400 text-xs">{t('notifications_screen.subtitle')}</p>
          </div>
        </div>
        <button 
          onClick={loadPrefs}
          disabled={loading}
          className="p-2 bg-zinc-800 rounded-lg"
        >
          <RefreshCw className={`w-5 h-5 text-zinc-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="bg-zinc-800/50 rounded-2xl p-8 text-center">
          <RefreshCw className="w-8 h-8 text-yellow-400 animate-spin mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">{t('notifications_screen.loading')}</p>
        </div>
      ) : (
        <>
          {/* Info Card */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-blue-300 text-sm">
              {t('notifications_screen.info')}
            </p>
          </div>

          {/* Section: Termine */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 px-1">
              <Calendar className="w-4 h-4 text-yellow-400" />
              <h2 className="text-white font-semibold text-sm">{t('notifications_screen.apt_notifications')}</h2>
            </div>
            
            <SettingRow
              icon={Mail}
              iconColor="bg-blue-500/20 text-blue-400"
              title={t('notifications_screen.email_confirm')}
              description={t('notifications_screen.email_confirm_hint')}
              prefKey="email"
            />
            
            <SettingRow
              icon={MessageCircle}
              iconColor="bg-green-500/20 text-green-400"
              title={t('notifications_screen.whatsapp_confirm')}
              description={t('notifications_screen.whatsapp_confirm_hint')}
              prefKey="whatsapp"
            />
          </div>

          {/* Section: Erinnerungen */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 px-1">
              <Bell className="w-4 h-4 text-yellow-400" />
              <h2 className="text-white font-semibold text-sm">{t('notifications_screen.apt_reminders')}</h2>
            </div>
            
            <SettingRow
              icon={Mail}
              iconColor="bg-blue-500/20 text-blue-400"
              title={t('notifications_screen.email_reminders')}
              description={t('notifications_screen.email_reminders_hint')}
              prefKey="email_reminders"
            />
            
            <SettingRow
              icon={MessageCircle}
              iconColor="bg-green-500/20 text-green-400"
              title={t('notifications_screen.whatsapp_reminders')}
              description={t('notifications_screen.whatsapp_reminders_hint')}
              prefKey="whatsapp_reminders"
            />
          </div>

          {/* Section: Newsletter */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 px-1">
              <Megaphone className="w-4 h-4 text-yellow-400" />
              <h2 className="text-white font-semibold text-sm">{t('notifications_screen.newsletter')}</h2>
            </div>
            
            <SettingRow
              icon={Mail}
              iconColor="bg-purple-500/20 text-purple-400"
              title={t('notifications_screen.email_newsletter')}
              description={t('notifications_screen.newsletter_hint')}
              prefKey="newsletter_email"
            />
            
            <SettingRow
              icon={MessageCircle}
              iconColor="bg-green-500/20 text-green-400"
              title={t('notifications_screen.whatsapp_newsletter')}
              description={t('notifications_screen.newsletter_hint')}
              prefKey="newsletter_whatsapp"
            />
          </div>

          {/* Section: Push (Coming Soon) */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 px-1">
              <Smartphone className="w-4 h-4 text-yellow-400" />
              <h2 className="text-white font-semibold text-sm">{t('notifications_screen.push_section')}</h2>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-zinc-800/30 rounded-xl opacity-60">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-zinc-700/50 text-zinc-500">
                  <Smartphone className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-zinc-400 font-medium text-sm">{t('notifications_screen.push_messages')}</p>
                  <p className="text-zinc-600 text-xs">{t('notifications_screen.push_soon')}</p>
                </div>
              </div>
              <span className="text-xs text-zinc-500 bg-zinc-700/50 px-2 py-1 rounded">{t('notifications_screen.push_badge')}</span>
            </div>
          </div>

          {/* Save Button */}
          <button
            onClick={savePrefs}
            disabled={saving}
            className="w-full py-4 bg-yellow-500 text-black rounded-xl font-bold flex items-center justify-center gap-2 mt-6"
            data-testid="save-notifications-btn"
          >
            {saving ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Save className="w-5 h-5" />
                {t('notifications_screen.save_settings')}
              </>
            )}
          </button>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-3 pt-4">
            <button
              onClick={() => setPrefs({
                email: true, whatsapp: true, email_reminders: true, 
                whatsapp_reminders: true, newsletter_email: true, 
                newsletter_whatsapp: true, push: false
              })}
              className="p-3 bg-zinc-800/50 rounded-xl text-center"
            >
              <Check className="w-5 h-5 text-green-400 mx-auto mb-1" />
              <p className="text-white text-xs font-medium">{t('notifications_screen.enable_all')}</p>
            </button>
            <button
              onClick={() => setPrefs({
                email: false, whatsapp: false, email_reminders: false, 
                whatsapp_reminders: false, newsletter_email: false, 
                newsletter_whatsapp: false, push: false
              })}
              className="p-3 bg-zinc-800/50 rounded-xl text-center"
            >
              <Bell className="w-5 h-5 text-red-400 mx-auto mb-1 line-through" />
              <p className="text-white text-xs font-medium">{t('notifications_screen.disable_all')}</p>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationSettingsScreen;
